import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Modal Component (Enhanced for NuVola Website Migration)
 * 
 * A comprehensive modal component covering all modal patterns from the original design.
 * Supports video modals, share modals, and all standard modal variants.
 */

const Modal = ({
  isOpen,
  onClose,
  children,
  size = 'default',
  variant = 'default',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  overlayClassName = '',
  ...props
}) => {
  const modalRef = useRef(null);

  // Handle escape key press
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Base overlay styles
  const overlayStyles = [
    'fixed',
    'inset-0',
    'z-50',
    'flex',
    'items-center',
    'justify-center',
    'bg-black',
    'bg-opacity-50',
    'backdrop-blur-sm',
    'p-4'
  ];

  // Size styles based on design system and original patterns
  const sizeStyles = {
    small: [
      'max-w-sm',
      'w-full'
    ],
    default: [
      'max-w-md',
      'w-full'
    ],
    medium: [
      'max-w-lg',
      'w-full'
    ],
    large: [
      'max-w-2xl',
      'w-full'
    ],
    xlarge: [
      'max-w-4xl',
      'w-full'
    ],
    fullscreen: [
      'max-w-full',
      'w-full',
      'h-full',
      'm-0',
      'rounded-none'
    ],
    // Original design sizes
    video: [
      'max-w-3xl',      // 800px from original
      'w-full',
      'mx-auto',
      'my-8'
    ],
    share: [
      'max-w-lg',       // 500px from original  
      'w-full',
      'mx-auto',
      'my-8'
    ]
  };

  // Variant styles based on original design patterns
  const variantStyles = {
    default: [
      'bg-white',
      'rounded-lg',
      'shadow-xl'
    ],
    
    // Video modal pattern (HomePage and VideoVault)
    video: [
      'bg-transparent',  // Transparent background for video
      'border-0',        // No border
      'shadow-none'      // No shadow for video content
    ],
    
    // Share modal pattern (HomePage)
    share: [
      'bg-white',
      'border',
      'border-gray-300',
      'rounded-3xl',     // 24px border radius from original
      'shadow-lg',       // Enhanced shadow: 0 4px 20px rgba(0,0,0,0.1)
      'p-5'              // 20px padding from original
    ]
  };

  // Base modal styles
  const modalStyles = [
    'transform',
    'transition-all',
    'duration-300',
    'max-h-full',
    'overflow-y-auto',
    'focus:outline-none'
  ];

  // Apply variant-specific size if variant has size
  const appliedSize = ['video', 'share'].includes(variant) ? variant : size;

  // Safety check: fallback to 'default' if appliedSize doesn't exist in sizeStyles
  const safeSize = sizeStyles[appliedSize] ? appliedSize : 'default';

  // Combine modal classes
  const modalClasses = [
    ...modalStyles,
    ...variantStyles[variant],
    ...sizeStyles[safeSize],
    className
  ].filter(Boolean).join(' ');

  // Combine overlay classes
  const overlayClasses = [
    ...overlayStyles,
    overlayClassName
  ].filter(Boolean).join(' ');

  // Show close button logic based on variant
  const shouldShowCloseButton = showCloseButton && variant !== 'video';

  return (
    <div
      className={overlayClasses}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={modalClasses}
        tabIndex="-1"
        {...props}
      >
        {shouldShowCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-nuvola-blue rounded z-10"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

// Modal subcomponents for better composition
const ModalHeader = ({ children, variant = 'default', className = '', showDivider = true, ...props }) => {
  // Variant-specific header styles
  const variantClasses = {
    default: `px-6 py-4 ${showDivider ? 'border-b border-gray-200' : ''}`,
    share: 'text-center mb-6',  // Share modal header styling
    video: 'p-0'               // Video modal has no header padding
  };

  return (
    <div 
      className={`${variantClasses[variant]} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

const ModalTitle = ({ children, level = 'h2', variant = 'default', className = '', ...props }) => {
  const Component = level;
  
  // Variant-specific title styles
  const variantClasses = {
    default: 'text-lg font-semibold text-gray-900',
    share: 'text-3xl font-semibold text-gray-800',    // 32px font, 600 weight from original
    video: 'sr-only'                                   // Hidden for video modals
  };

  return (
    <Component 
      id="modal-title"
      className={`${variantClasses[variant]} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

const ModalBody = ({ children, variant = 'default', className = '', ...props }) => {
  // Variant-specific body styles
  const variantClasses = {
    default: 'px-6 py-4',
    share: '',                // Share modal body has no default padding (handled by variant padding)
    video: 'p-0'             // Video modal body has no padding
  };

  return (
    <div className={`${variantClasses[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
};

const ModalContent = ({ children, variant = 'default', className = '', ...props }) => {
  // Variant-specific content styles
  const variantClasses = {
    default: 'text-gray-600 leading-relaxed',
    share: 'text-gray-800',
    video: 'w-full'
  };

  return (
    <div className={`${variantClasses[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
};

const ModalFooter = ({ 
  children, 
  variant = 'default',
  className = '', 
  align = 'right',
  showDivider = true,
  ...props 
}) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between'
  };

  // Variant-specific footer styles
  const variantClasses = {
    default: `px-6 py-4 ${showDivider ? 'border-t border-gray-200' : ''} flex items-center gap-3`,
    share: 'flex justify-center gap-3 mt-4',
    video: 'p-0'
  };

  return (
    <div 
      className={`${variantClasses[variant]} ${alignClasses[align]} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Video-specific modal components
const VideoModal = ({ isOpen, onClose, videoSrc, poster, className = '', ...props }) => {
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      variant="video"
      showCloseButton={false}
      className={className}
      {...props}
    >
      <ModalBody variant="video">
        <video 
          controls
          autoPlay
          className="w-full h-auto"
          poster={poster}
          src={videoSrc}
        />
      </ModalBody>
    </Modal>
  );
};

// Share-specific modal components  
const ShareModal = ({ 
  isOpen, 
  onClose, 
  title = "Share", 
  url, 
  shareText = "Check out this amazing content from NuVola AI!", 
  videoTitle = "",
  className = '', 
  children, 
  ...props 
}) => {
  const copyToClipboard = () => {
    if (url) {
      navigator.clipboard.writeText(url)
        .then(() => {
          alert('Link copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };

  // Social media sharing functions
  const shareToFacebook = () => {
    if (!url) return;
    
    const shareUrl = encodeURIComponent(url);
    const text = encodeURIComponent(shareText + (videoTitle ? ` - ${videoTitle}` : ''));
    const hashtag = encodeURIComponent('#NuVolaAI');
    
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${text}&hashtag=${hashtag}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    if (!url) return;
    
    const shareUrl = encodeURIComponent(url);
    const text = encodeURIComponent(shareText + (videoTitle ? ` - ${videoTitle}` : '') + ' #NuVolaAI #ScamPrevention');
    
    const twitterUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=${text}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const shareToReddit = () => {
    if (!url) return;
    
    const shareUrl = encodeURIComponent(url);
    const redditTitle = encodeURIComponent(videoTitle || shareText);
    
    const redditUrl = `https://www.reddit.com/submit?url=${shareUrl}&title=${redditTitle}`;
    window.open(redditUrl, '_blank', 'width=800,height=600');
  };

  const shareToEmail = () => {
    if (!url) return;
    
    const subject = encodeURIComponent(videoTitle || 'Check out this content from NuVola AI');
    const body = encodeURIComponent(`${shareText}\n\n${videoTitle ? `${videoTitle}\n` : ''}${url}\n\nStay safe online with NuVola AI!`);
    
    const emailUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = emailUrl;
  };

  // Social media platforms configuration
  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: (
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      bgColor: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      onClick: shareToFacebook
    },
    {
      name: 'X',
      icon: (
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      bgColor: 'bg-black',
      hoverColor: 'hover:bg-gray-800',
      onClick: shareToTwitter
    },
    {
      name: 'Reddit',
      icon: (
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      ),
      bgColor: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      onClick: shareToReddit
    },
    {
      name: 'Email',
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
      ),
      bgColor: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      onClick: shareToEmail
    }
  ];

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      variant="share"
      className={className}
      {...props}
    >
      <ModalHeader variant="share">
        <ModalTitle variant="share">{title}</ModalTitle>
      </ModalHeader>
      <ModalBody variant="share">
        {/* Social Media Icons */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {socialPlatforms.map((platform) => (
            <div 
              key={platform.name}
              className="text-center cursor-pointer group"
              onClick={platform.onClick}
            >
              <div className={`w-12 h-12 ${platform.bgColor} ${platform.hoverColor} rounded-full flex items-center justify-center mx-auto mb-2 transition-colors duration-300`}>
                {platform.icon}
              </div>
              <p className="text-sm text-gray-600">{platform.name}</p>
            </div>
          ))}
        </div>
        
        {/* Custom children content */}
        {children}
        
        {/* Copy Link Section */}
        {url && (
          <div className="mt-5">
            <div 
              className="flex items-center justify-center bg-gray-100 rounded-lg p-3 mb-4 cursor-pointer hover:bg-gray-200 transition-colors duration-300"
              onClick={copyToClipboard}
            >
              <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
              <span className="text-gray-700 font-medium">Copy link</span>
            </div>
            <input 
              type="text" 
              value={url} 
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-gray-50"
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

// Confirmation Modal variant
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
  ...props
}) => {
  // Note: Removed unused variantStyles variable that was causing ESLint warning
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small" {...props}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <ModalContent>{message}</ModalContent>
      </ModalBody>
      <ModalFooter>
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors duration-200"
          disabled={loading}
        >
          {cancelText}
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={`px-4 py-2 text-white rounded transition-colors duration-200 ${
            variant === 'danger' 
              ? 'bg-error hover:bg-error-hover' 
              : variant === 'warning'
              ? 'bg-warning hover:bg-yellow-600'
              : 'bg-nuvola-blue hover:bg-nuvola-blue-hover'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Loading...' : confirmText}
        </button>
      </ModalFooter>
    </Modal>
  );
};

// PropTypes for main Modal component
Modal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool.isRequired,
  
  /** Function to call when modal should close */
  onClose: PropTypes.func.isRequired,
  
  /** Modal content */
  children: PropTypes.node.isRequired,
  
  /** Modal size */
  size: PropTypes.oneOf(['small', 'default', 'large', 'xlarge', 'fullscreen', 'video', 'share']),
  
  /** Modal variant for different styling patterns */
  variant: PropTypes.oneOf(['default', 'video', 'share']),
  
  /** Show close button */
  showCloseButton: PropTypes.bool,
  
  /** Close modal when clicking backdrop */
  closeOnBackdropClick: PropTypes.bool,
  
  /** Close modal on escape key */
  closeOnEscape: PropTypes.bool,
  
  /** Additional CSS classes for modal content */
  className: PropTypes.string,
  
  /** Additional CSS classes for overlay */
  overlayClassName: PropTypes.string
};

// PropTypes for VideoModal
VideoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  videoSrc: PropTypes.string.isRequired,
  poster: PropTypes.string,
  className: PropTypes.string
};

// PropTypes for ShareModal
ShareModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  url: PropTypes.string,
  shareText: PropTypes.string,
  videoTitle: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node
};

// PropTypes for ConfirmModal
ConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'danger', 'warning']),
  loading: PropTypes.bool
};

// PropTypes for enhanced subcomponents
ModalHeader.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'share', 'video']),
  className: PropTypes.string,
  showDivider: PropTypes.bool
};

ModalTitle.propTypes = {
  children: PropTypes.node.isRequired,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  variant: PropTypes.oneOf(['default', 'share', 'video']),
  className: PropTypes.string
};

ModalBody.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'share', 'video']),
  className: PropTypes.string
};

ModalContent.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'share', 'video']),
  className: PropTypes.string
};

ModalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'share', 'video']),
  className: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right', 'between']),
  showDivider: PropTypes.bool
};

// Export main component and subcomponents
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.Confirm = ConfirmModal;
Modal.Video = VideoModal;
Modal.Share = ShareModal;

export default Modal; 